import { IUncountedBand } from '../models/stock-take-dashboard.interface';
import { parseStockTakeDateToMillis } from './stock-take-stock-date.utils';

/**
 * Date-metric contribution of items that report to another item.
 * Dashboard last-counted totals subtract these so only countable items are included.
 */
export interface IReportToItemMetricAdjustment {
  bandStaleCounts: number[];
  itemCount: number;
  neverCountedCount: number;
  neverCountedStockValue: number;
  totalStockValue: number;
  valueCountedInCoveragePeriod: number;
}

/**
 * Zeroed adjustment sized to the dashboard uncounted bands.
 */
export function createEmptyReportToItemMetricAdjustment(bandCount: number): IReportToItemMetricAdjustment {
  return {
    bandStaleCounts: Array.from({ length: bandCount }, () => 0),
    itemCount: 0,
    neverCountedCount: 0,
    neverCountedStockValue: 0,
    totalStockValue: 0,
    valueCountedInCoveragePeriod: 0,
  };
}

/**
 * Add one report-to item into the adjustment using the same date rules as the dashboard aggregates.
 */
export function applyReportToItemToMetricAdjustment(
  adjustment: IReportToItemMetricAdjustment,
  rawDate: unknown,
  stockValue: number,
  neverCountedFloorMs: number,
  coverageCutoffMs: number,
  bandCutoffMs: readonly number[],
): void {
  const safeValue = Number.isFinite(stockValue) ? stockValue : 0;
  adjustment.itemCount += 1;
  adjustment.totalStockValue += safeValue;
  const dateMs = parseStockTakeDateToMillis(rawDate);
  if (dateMs == null || dateMs < neverCountedFloorMs) {
    adjustment.neverCountedCount += 1;
    adjustment.neverCountedStockValue += safeValue;
    return;
  }
  if (dateMs >= coverageCutoffMs) {
    adjustment.valueCountedInCoveragePeriod += safeValue;
  }
  bandCutoffMs.forEach((cutoffMs: number, index: number) => {
    if (dateMs < cutoffMs) {
      adjustment.bandStaleCounts[index] = (adjustment.bandStaleCounts[index] ?? 0) + 1;
    }
  });
}

/**
 * Remove report-to items from store-wide uncounted bands.
 * Age bands already include the never-counted total, so both parts are removed.
 */
export function subtractReportToItemsFromUncountedBands(
  bands: IUncountedBand[],
  adjustment: IReportToItemMetricAdjustment,
  bandsAvailable: boolean,
): IUncountedBand[] {
  return bands.map((band: IUncountedBand, index: number) => {
    if (band.label === 'Never') {
      return {
        ...band,
        count: Math.max(0, band.count - adjustment.neverCountedCount),
      };
    }
    if (!bandsAvailable) {
      return band;
    }
    const staleRemoved = adjustment.bandStaleCounts[index] ?? 0;
    return {
      ...band,
      count: Math.max(0, band.count - adjustment.neverCountedCount - staleRemoved),
    };
  });
}
